import { invariant } from '@epic-web/invariant'
import {
  type Block,
  type BlockChange,
  type ListBlock,
  type BlockByType,
  type RichTextBlock,
  type OgImageBlock,
} from '.'
import type { TreeManager } from '@basehub/utils/tree/index'
import { getIdempotentId } from '@basehub/utils/id/get-idempotent-id'

import type { AtLeast } from '../../helpers'
import {
  jsonToPlainText,
  plainTextToYDocBase64State,
  richTextExtensionsForTransformations,
  stateToJson,
} from '@basehub/tiptap-api-utils'
import {
  BlockRelationship,
  getBRId,
  isStructuralBr,
} from '../block-relationship'

const idToSourceBlockId = (b: Block) => b.sourceBlockId ?? b.id

export type LiteBlock = AtLeast<
  Block,
  | 'id'
  | 'type'
  | 'title'
  | 'apiName'
  | 'slug'
  | 'value'
  | 'typeOptions'
  | 'sourceBlockId'
  | 'instanceChildTarget'
>

export function getInstanceChildId({
  parentSourceBlockId,
  targetSourceBlockId,
}: {
  parentSourceBlockId: string
  targetSourceBlockId: string
}) {
  return getIdempotentId({
    idSeed: `${parentSourceBlockId}-${targetSourceBlockId}`,
  })
}

/**
 * This function takes an instance's child (which doesn't have ANY structure)
 * and gets its target (from the target component) and adds the structure into it.
 *
 * Remember: instance children only care about their value. then they just inherit the rest from the target.
 *
 * If the block (instance's child) is `null`, then we'll create a "virtualized" block with a deterministic ID.
 */
export function makeInstanceChildBlock<B extends LiteBlock>({
  block,
  parentSourceBlockId,
  targetBlock,
}: {
  block: B | null
  parentSourceBlockId: string
  targetBlock: B
}): B & { __virtual?: boolean } {
  const targetSourceBlockId =
    targetBlock.instanceChildTarget ||
    targetBlock.sourceBlockId ||
    targetBlock.id
  const __virtualInstanceChildTarget =
    targetBlock.sourceBlockId || targetBlock.id
  invariant(targetSourceBlockId, 'targetSourceBlockId is required')

  const base = {
    title: targetBlock.title,
    constraints: targetBlock.constraints,
    searchConfig: targetBlock.searchConfig,
    apiName: targetBlock.apiName,
    slug: targetBlock.slug,
    description: targetBlock.description,
    type: targetBlock.type === 'component' ? 'instance' : targetBlock.type,
    typeOptions:
      targetBlock.type === 'component'
        ? { targetBlockId: targetSourceBlockId }
        : targetBlock.typeOptions,
    instanceChildTarget: targetSourceBlockId,
    // intentionally not copying hidden, as it shouldn't depend on the target block, but rather, on the parent
    // blockClone.hidden = targetBlock.hidden
  }

  let blockClone: B | null = null
  if (block && ('__virtual' in block === false || !block?.__virtual)) {
    blockClone = { ...block, ...base } as B
  } else {
    const sourceBlockId = getInstanceChildId({
      parentSourceBlockId,
      targetSourceBlockId,
    })
    blockClone = {
      ...(targetBlock as any),
      ...(base as any),
      hidden: false,
      isCommitted: false,
      id: sourceBlockId,
      sourceBlockId,
      value:
        targetBlock.type === 'og-image'
          ? ({} satisfies OgImageBlock['value'])
          : targetBlock.value,
      __virtual: true,
      __virtualInstanceChildTarget,
    } as B & { __virtual?: boolean; __virtualInstanceChildTarget?: string }
  }

  if (targetBlock.type === 'component') {
    invariant(
      blockClone.type === 'instance',
      `Block should be an instance block`
    )
  } else {
    invariant(
      blockClone.type === targetBlock.type,
      `Block type should match. ${blockClone.type} !== ${targetBlock.type}`
    )
  }

  if (targetBlock.type === 'list' && targetBlock.value?.views) {
    invariant(
      blockClone.type === 'list',
      `Block type should match. ${blockClone.type} !== ${targetBlock.type}`
    )
    if (blockClone.value) {
      blockClone.value = {
        ...(blockClone.value as ListBlock['value']),
        views: targetBlock.value.views,
      }
    }
  }

  return blockClone
}

export function makeVirtualizedInstanceChildBR({
  rootBlockId,
  parentBlock,
  virtualizedBlock,
  targetBR,
}: {
  rootBlockId: string
  parentBlock: Block
  virtualizedBlock: Block
  targetBR: BlockRelationship
}) {
  const newBr: BlockRelationship = {
    validFrom: targetBR.validFrom,
    validTo: targetBR.validTo,
    branchId: targetBR.branchId,
    blockId: virtualizedBlock.id,
    id: getBRId(virtualizedBlock.id, rootBlockId),
    isCommitted: virtualizedBlock.isCommitted,
    order: targetBR.order,
    parentId: idToSourceBlockId(parentBlock),
    sourceBlockId: idToSourceBlockId(virtualizedBlock),
    deletedAt: null,
    structural: isStructuralBr({
      parent: parentBlock,
      block: virtualizedBlock,
    }),
    // @ts-ignore
    __virtual: true,
  }
  return newBr
}

// const idToSourceBlockId = (b: Block) => b.sourceBlockId || b.id

export function makeInstanceChildBlockChange({
  child, // treeNew,
  // treeOld,
}: {
  child: BlockChange
  treeNew: TreeManager<Block, any> | undefined
  treeOld: TreeManager<Block, any> | undefined | false | null
}): BlockChange {
  // this function (i think) is pointless, as we're already running makeInstanceChildBlock
  // when we create the tree manager in the first place.
  // ALSO, it's buggy when calculating merge diffs, as it doesn't take into account the treeCommonAncestor
  // so we disable it for now.
  return child

  // const debug = child.id === 'a74a6a1cd59e7a2acebd2'
  // const prevNode =
  //   child.previous && treeOld
  //     ? treeOld.getNode(idToSourceBlockId(child.previous))
  //     : undefined
  // const prevParentNode =
  //   prevNode?.parentKey && treeOld
  //     ? treeOld.getNode(prevNode.parentKey)
  //     : undefined
  // const currentNode =
  //   child.current && treeNew
  //     ? treeNew.getNode(idToSourceBlockId(child.current))
  //     : undefined
  // const currentParentNode =
  //   currentNode?.parentKey && treeNew
  //     ? treeNew.getNode(currentNode.parentKey)
  //     : undefined

  // let prevEnhanced = prevNode?.value
  // let currentEnhanced = currentNode?.value

  // let hasEnhanced = false

  // if (debug) {
  //   console.log({
  //     willPassFirstCheck: child.previous && treeOld,
  //     treeOld,
  //     prevNode,
  //     prevParentNode,
  //     currentNode,
  //     currentParentNode,
  //   })
  // }

  // if (prevParentNode?.value.type === 'instance' && prevNode?.value && treeOld) {
  //   hasEnhanced = true
  //   invariant(
  //     prevNode.value.instanceChildTarget,
  //     'instanceChildTarget is required'
  //   )
  //   const targetBlock =
  //     treeOld.getNode(prevNode.value.instanceChildTarget, {
  //       fallbackKey: 'sourceBlockId',
  //     })?.value ??
  //     treeNew?.getNode(prevNode.value.instanceChildTarget, {
  //       fallbackKey: 'sourceBlockId',
  //     })?.value
  //   if (targetBlock) {
  //     prevEnhanced = makeInstanceChildBlock({
  //       parentSourceBlockId:
  //         prevParentNode.value.sourceBlockId || prevParentNode.value.id,
  //       block: prevNode.value,
  //       targetBlock,
  //     })
  //   }
  // }
  // if (
  //   currentParentNode?.value.type === 'instance' &&
  //   currentNode?.value &&
  //   treeNew
  // ) {
  //   hasEnhanced = true
  //   invariant(
  //     currentNode.value.instanceChildTarget,
  //     'instanceChildTarget is required'
  //   )
  //   const targetBlock = treeNew.getNode(currentNode.value.instanceChildTarget, {
  //     fallbackKey: 'sourceBlockId',
  //   })?.value
  //   if (targetBlock) {
  //     currentEnhanced = makeInstanceChildBlock({
  //       parentSourceBlockId: idToSourceBlockId(currentParentNode.value),
  //       block: currentNode.value,
  //       targetBlock,
  //     })
  //   }
  // }

  // if (hasEnhanced) {
  //   return {
  //     ...child,
  //     title: currentEnhanced?.title ?? prevEnhanced?.title ?? child.title,
  //     previous: prevEnhanced ?? null,
  //     current: currentEnhanced ?? null,
  //   } as BlockChange
  // }

  // return child
}

type BlockMigrationResult = AtLeast<Block, 'type'>

export async function migrateCurrentBlockToNewType({
  currentBlock,
  targetBlock,
}: {
  currentBlock: AtLeast<Block, 'type' | 'value'>
  targetBlock: Block
}): Promise<BlockMigrationResult> {
  switch (currentBlock.type) {
    case 'rich-text':
      if (targetBlock.type !== 'text') {
        throw new Error('Cannot convert rich text to non-text block')
      }
      return {
        type: 'text',
        value:
          typeof currentBlock.value === 'string'
            ? // seems to already be a plain text block
              currentBlock.value
            : (jsonToPlainText(
                stateToJson((currentBlock as RichTextBlock).value.state),
                richTextExtensionsForTransformations
              ) satisfies BlockByType<'text'>['value']),
        constraints: targetBlock.constraints,
      }
    case 'text':
      if (targetBlock.type !== 'rich-text') {
        throw new Error('Cannot convert text to non-rich text block')
      }
      return {
        type: 'rich-text',
        value:
          typeof currentBlock.value === 'object' &&
          currentBlock.value !== null &&
          'state' in (currentBlock.value as any)
            ? // seems to already be a rich text block
              (currentBlock.value as unknown as BlockByType<'rich-text'>['value'])
            : ({
                state: (
                  await plainTextToYDocBase64State(
                    currentBlock.value ?? '',
                    richTextExtensionsForTransformations
                  )
                ).base64,
                lastRevertedAt: new Date().toISOString(),
              } satisfies BlockByType<'rich-text'>['value']),
        constraints: targetBlock.constraints,
      }
    default:
      throw new Error(
        `Cannot convert ${currentBlock.type} block to ${targetBlock.type}`
      )
  }
}

export * from './instance_old'

export function isVirtualInstanceChildTarget<B extends LiteBlock>(
  block: B
): block is B & {
  __virtual: true
  __virtualInstanceChildTarget: string
  instanceChildTarget: string
} {
  return block && '__virtual' in block && block.__virtual === true
}

export function isVirtualizedInstanceChildTargetBR(
  br: BlockRelationship
): br is BlockRelationship & { __virtual: true } {
  return '__virtual' in br && br.__virtual === true
}
